import React, { useState, useRef, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans } from "@lingui/macro";

import {
  Popover,
  POPOVER_POSITIONS,
  POPOVER_TRIGGERS
} from "../popover/index.jsx";
import { focusAtInputEnd } from "@widgets/common/libs/util.js";

import styles from "./card.tooltip.css";

export const CARD_TOOLTIP_POSITIONS = { ...POPOVER_POSITIONS };

export const CARD_TOOLTIP_TRIGGERS = { ...POPOVER_TRIGGERS };

export const FOOTER_ACTION_ALIGN = {
  LEFT: "left",
  RIGHT: "right"
};

export const FOOTER_ACTION_BTN_TYPE = {
  PRIMARY: "primary",
  SECONDARY: "secondary"
};

export const CardTooltip = ({
  position = CARD_TOOLTIP_POSITIONS.BOTTOM,
  trigger = [CARD_TOOLTIP_TRIGGERS.HOVER],
  className,
  targetRenderer = () => {},
  children,
  title,
  content = "",
  canEdit = false,
  placeholder,
  maxLength = 250,
  emptyText,
  onCancel = () => {},
  onSave = () => {},
  validateSaveBtn = false,
  getContainer = () => document.body
}) => {
  const [isEdit, setEdit] = useState(false);
  const [stateContent, setStateContent] = useState(content);

  const canSave = validateSaveBtn ? stateContent.trim() : true;

  const isSaveTrigger = useRef(false);
  let contentRef = useRef();

  useEffect(
    function updateContent() {
      setStateContent(content);
    },
    [content]
  );

  useEffect(
    function focusInputFeild() {
      if (isEdit && contentRef.current) {
        focusAtInputEnd(contentRef.current);
      }
    },
    [isEdit]
  );

  const autoSize = () => {
    requestAnimationFrame(() => {
      let el = contentRef.current;
      if (!el) return;
      el.style.cssText = "height:auto";
      el.style.cssText = "height:" + el.scrollHeight + "px";
      el.style.overflow = "hidden";
    });
  };

  const slots = React.Children.map(children, (child) => child) || [];

  const footerPanel = slots.find((child) => child.type === CardTooltip.Footer);

  return (
    <Popover
      position={position}
      trigger={isEdit ? [CARD_TOOLTIP_TRIGGERS.CLICK] : trigger}
      destroyOnClose={true}
      onClose={() => {
        setEdit(false);
        !isSaveTrigger.current && setStateContent(content);
      }}
      className={`${isEdit ? styles.popoverClass : ""}`}
      targetRenderer={targetRenderer}
      getContainer={getContainer}
    >
      <div
        className={`${styles.cardTooltipContainer} ${className} cardToolTipClass`}
      >
        {title && <div className={`small600 ${styles.title}`}>{title}</div>}
        {!isEdit ? (
          <div
            className={`small ${styles.content} ${
              !stateContent ? styles.emptyText : ""
            }`}
          >
            {stateContent || emptyText}
          </div>
        ) : (
          <textarea
            rows="1"
            cols="100"
            value={stateContent}
            autoFocus={true}
            ref={contentRef}
            maxLength={maxLength}
            onFocus={autoSize}
            onChange={(e) => {
              let newvalue = e.target.value;
              setStateContent(newvalue);
              autoSize();
            }}
            className={`small ${styles.textAreaField}`}
            onKeyDown={(e) => {
              autoSize();
            }}
            placeholder={placeholder}
          />
        )}

        {footerPanel && <div>{footerPanel}</div>}
        {!footerPanel && canEdit && (
          <div className={`${styles.footerContainer} ${styles.right}`}>
            {!isEdit ? (
              <div
                className={`small ${styles.primary}`}
                onClick={() => {
                  setEdit(true);
                  isSaveTrigger.current = false;
                }}
              >
                {stateContent ? <Trans>Edit</Trans> : <Trans>Add</Trans>}
              </div>
            ) : (
              <div
                className={`${styles.footerRightContainer}  ${styles.right}`}
              >
                <div
                  className={`small ${styles.secondary}`}
                  onClick={() => {
                    setEdit(false);
                    setStateContent(content);
                    onCancel();
                  }}
                >
                  <Trans>Cancel</Trans>
                </div>
                <div
                  className={`small ${styles.primary}  ${
                    !canSave ? styles.disabled : ""
                  }`}
                  onClick={() => {
                    if (!canSave) return;
                    setEdit(false);
                    isSaveTrigger.current = true;
                    onSave(stateContent);
                  }}
                >
                  <Trans>Save</Trans>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Popover>
  );
};

CardTooltip.propTypes = {
  /**
   * It will indicates position of the cardtooltip,like left,right etc.
   */
  position: PropTypes.oneOf(Object.values(CARD_TOOLTIP_POSITIONS)),
  trigger: PropTypes.oneOf(Object.values(CARD_TOOLTIP_TRIGGERS)),
  /**
   * show title of the cardtooltip
   */
  title: PropTypes.string,
  /**
   * show content of the cardtooltip
   */
  content: PropTypes.node.isRequired,
  /**
   * canEdit is a bool,it will indicates whether user can able to edit or not.
   */
  canEdit: PropTypes.bool,
  /**
   * Placeholder of cardtooltip.
   */
  placeholder: PropTypes.string,
  /**
   * It will cancel the edit option for cardtooltip.
   */
  onCancel: PropTypes.func,
  /**
   * It will save the data.
   */
  onSave: PropTypes.func,
  targetRenderer: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  emptyText: PropTypes.string,
  /**
   * It will indicates how many letters user enter in tooltip.
   */
  maxLength: PropTypes.number,
  /**
   *  To disable the save button, if the content is empty while editing
   */
  validateSaveBtn: PropTypes.bool,
  /**
   * Function that returns the DOM container for the modal.
   */
  getContainer: PropTypes.func
};

CardTooltip.defaultProps = {
  position: CARD_TOOLTIP_POSITIONS.BOTTOM,
  trigger: [CARD_TOOLTIP_TRIGGERS.HOVER],
  targetRenderer: () => {},
  canEdit: false,
  maxLength: 250,
  onCancel: () => {},
  onSave: () => {},
  content: "",
  validateSaveBtn: false
};

function CardTooltipFooterPanel(props) {
  const slots = React.Children.map(props.children, (child) => child) || [];
  const footerActionsBtn = slots.filter(
    (child) => child.type === CardTooltip.FooterActionsBtn
  );
  return (
    <div className={`${styles.footerContainer} ${styles[props.align]}`}>
      {React.Children.map(footerActionsBtn, (action, index) => {
        return (
          <div
            key={index}
            className={`small ${styles[action.props.type]}`}
            onClick={action.props.onClick}
          >
            {action.props.text}
          </div>
        );
      })}
    </div>
  );
}

CardTooltipFooterPanel.propTypes = {
  children: PropTypes.node,
  align: PropTypes.string
};

CardTooltip.Footer = CardTooltipFooterPanel;
CardTooltip.Footer.displayName = "CardTooltip.Footer";

function CardTooltipFooterActionsBtn(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
CardTooltipFooterActionsBtn.propTypes = {
  children: PropTypes.node
};

CardTooltip.FooterActionsBtn = CardTooltipFooterActionsBtn;
CardTooltip.FooterActionsBtn.displayName = "CardTooltip.FooterActionsBtn";
